import React, { useLayoutEffect, useState, useCallback, useEffect } from 'react';
import { useAuth } from '../../Context/AuthContext';
import LayoutComponent from '../BaseScreen';
import TableComponent from '../../Components/Tables/TableComponent';
import ModernTableComponent from '../../Components/Tables/ModernTableComponent';
import IntakeForm from '../../Components/Forms/IntakeForm';
import { useIntake } from '../../Context/IntakeContext';
import { useData } from '../../Context/DataContext';
import ButtonComponent from '../../Components/Inputs/ButtonComponent';
import { historic2Options } from '../../Options/Historic2Options';
import { X } from 'react-feather';
import PatientDetailPanel from '../../Components/Modals/PatientDetailPanel';
import CallSheetModal from '../../Components/Modals/CallSheetModal';

const calculateLikelihood = (row: any): string => {
  const paidPercent = parseFloat(String(row.paidPercent || '0').replace(/%/g, '')) || 0;
  const avgDailyRate = parseFloat(String(row.avgDailyRate || '0').replace(/[$,]/g, '')) || 0;
  const numPatients = parseInt(row.numPatients) || 0;
  const avgUnits = parseFloat(String(row.avgUnits || '0').replace(/,/g, '')) || 0;

  let score = 0;
  if (paidPercent >= 70) score += 30;
  else if (paidPercent >= 50) score += 22;
  else if (paidPercent >= 30) score += 15;
  else if (paidPercent >= 15) score += 8;

  if (avgDailyRate >= 800) score += 30;
  else if (avgDailyRate >= 500) score += 22;
  else if (avgDailyRate >= 300) score += 15;
  else if (avgDailyRate >= 150) score += 8;

  if (numPatients >= 10) score += 20;
  else if (numPatients >= 5) score += 15;
  else if (numPatients >= 3) score += 10;
  else if (numPatients >= 1) score += 5;

  if (avgUnits >= 25) score += 20;
  else if (avgUnits >= 18) score += 15;
  else if (avgUnits >= 12) score += 10;
  else if (avgUnits >= 7) score += 5;

  if (score >= 75) return 'Highly Likely';
  if (score >= 50) return 'Likely';
  if (score >= 25) return 'Unlikely';
  return 'Very Unlikely';
};

const HomeScreen = () => {
  const { allUsers, currentProfile } = useAuth();
  const { showPrefix, hidePrefixPopup, prefixDataDashboard, handleSelectedPatientRecord, deleteIntakeRecord } = useIntake();
  const { filteredIntakeRecords, getIntakeRecords, intakeRecords } = useIntake();
  const { addRecord, handleAddRecord, externalData } = useData();
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isCallSheetOpen, setIsCallSheetOpen] = useState(false);

  // Handle delete row
  const handleDeleteRow = useCallback(async (record: any) => {
    if (window.confirm(`Are you sure you want to delete ${record.name}?`)) {
      try {
        await deleteIntakeRecord(record.intake_id);
      } catch (error) {
        console.error('Failed to delete record:', error);
      }
    }
  }, [deleteIntakeRecord]);

  useLayoutEffect(() => {
    getIntakeRecords()
  }, [])

  // Update selectedPatient when data refreshes (for real-time updates in the panel)
  useEffect(() => {
    if (selectedPatient && filteredIntakeRecords) {
      const updatedPatient = filteredIntakeRecords.find(
        (record: any) => record.intake_id === selectedPatient.intake_id
      );
      if (updatedPatient) {
        // Only update if data actually changed to avoid unnecessary re-renders
        const hasChanged = JSON.stringify(updatedPatient) !== JSON.stringify(selectedPatient);
        if (hasChanged) {
          setSelectedPatient(updatedPatient);
          handleSelectedPatientRecord(updatedPatient);
        }
      }
    }
  }, [filteredIntakeRecords])

  const isPrivileged = ['admin', 'owner', 'dev'].includes(currentProfile?.privileges || '');

  // Modern table column configuration
  const modernColumns = [
    { key: 'name', label: 'Client', type: 'text' as const, width: '140px' },
    { key: 'insurance', label: 'Insurance', type: 'text' as const, width: '180px' },
    { key: 'facility', label: 'Facility', type: 'badge' as const, badgeType: 'facility' as const },
    { key: 'checked_in', label: 'Status', type: 'status' as const },
    { key: 'booked', label: 'Stage', type: 'status' as const },
    ...(isPrivileged ? [
      { key: 'prefix', label: 'Prefix', type: 'text' as const, width: '80px' },
      { key: 'vob_likelihood', label: 'Likelihood', type: 'likelihood' as const, width: '120px' },
      { key: 'vob_paid_percent', label: 'Paid %', type: 'percent' as const, width: '80px' },
    ] : []),
    { key: 'last_update_booked', label: 'Updated', type: 'date' as const },
    { key: 'coordinatorName', label: 'Assignee', type: 'assignee' as const, photoKey: 'coordinatorPhoto' },
  ];

  const tabs = [
    { label: 'All Records', value: 'all' },
    { label: 'Pending', value: 'pending' },
    { label: 'Approved', value: 'approved' },
    { label: 'Booked', value: 'booked' },
    { label: 'Yellow Stripes', value: 'yellow_stripe' },
  ];

  // Tab filtering function for intake records
  const tabFilterFn = useCallback((row: any, tabValue: string) => {
    const checkedIn = String(row.checked_in || '').toLowerCase();
    const booked = String(row.booked || '').toLowerCase();

    switch (tabValue) {
      case 'pending':
        return checkedIn.includes('pending') || checkedIn === '' || !row.checked_in;
      case 'approved':
        return checkedIn.includes('approved') || checkedIn.includes('confirmed');
      case 'booked':
        return booked.includes('booked') || booked === 'yes' || booked === 'true';
      case 'yellow_stripe':
        return booked.includes('yellow stripe');
      default:
        return true;
    }
  }, []);

  // Get tab counts dynamically
  const getTabCount = useCallback((tabValue: string, data: any[]) => {
    if (tabValue === 'all') return data.length;
    return data.filter(row => tabFilterFn(row, tabValue)).length;
  }, [tabFilterFn]);

  // Handle row click to open patient detail panel
  const handleRowClick = useCallback((record: any) => {
    handleSelectedPatientRecord(record);
    setSelectedPatient(record);
    setIsPanelOpen(true);
  }, [handleSelectedPatientRecord]);

  // Close patient detail panel
  const handleClosePanel = useCallback(() => {
    setIsPanelOpen(false);
    setSelectedPatient(null);
  }, []);

  // Get coordinator info from UUID (handles multiple comma-separated IDs)
  const getCoordinatorInfo = useCallback((coordinatorId: string) => {
    if (!coordinatorId) return { name: '-', photo: null, count: 0, allUsers: [] as any[] };

    const ids = coordinatorId.split(',').filter(Boolean);
    if (ids.length === 0) return { name: '-', photo: null, count: 0, allUsers: [] as any[] };

    if (allUsers) {
      const coordinatorUsers = ids.map(id => allUsers.find((user: any) => user.userid === id)).filter(Boolean);
      const firstUser = coordinatorUsers[0];
      if (firstUser) {
        return {
          name: firstUser.name,
          photo: firstUser.profile_photo || null,
          count: ids.length,
          allUsers: coordinatorUsers
        };
      }
    }
    return { name: ids[0], photo: null, count: ids.length, allUsers: [] as any[] };
  }, [allUsers]);

  // Format name to show first name and last initial (e.g., "John S.")
  const formatNameWithLastInitial = (fullName: string) => {
    if (!fullName) return '';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0];
    const firstName = parts[0];
    const lastInitial = parts[parts.length - 1][0];
    return `${firstName} ${lastInitial}.`;
  };

  // Build VOB lookup by prefix (best paid %)
  const vobByPrefix = React.useMemo(() => {
    const map: Record<string, { paidPercent: string; likelihood: string }> = {};
    if (!externalData) return map;
    for (const row of externalData) {
      const prefix = (row.prefix || '').toUpperCase();
      if (!prefix) continue;
      const currentPaid = parseFloat(String(row.paidPercent || '0').replace(/%/g, '')) || 0;
      const existingPaid = map[prefix] ? parseFloat(String(map[prefix].paidPercent || '0').replace(/%/g, '')) : -1;
      if (currentPaid > existingPaid) {
        map[prefix] = {
          paidPercent: row.paidPercent || '—',
          likelihood: calculateLikelihood(row),
        };
      }
    }
    return map;
  }, [externalData]);

  // Transform data to include coordinator names and handle denied status
  const transformedData = React.useMemo(() => {
    if (!filteredIntakeRecords) return [];
    return filteredIntakeRecords.map(record => {
      const isDenied = String(record.checked_in || '').toLowerCase().includes('denied');
      const coordinatorInfo = getCoordinatorInfo(record.coordinator);
      const prefix = (record.prefix || '').toUpperCase();
      const vob = vobByPrefix[prefix];
      return {
        ...record,
        coordinatorName: formatNameWithLastInitial(coordinatorInfo.name),
        coordinatorPhoto: coordinatorInfo.photo,
        coordinatorCount: coordinatorInfo.count,
        coordinatorUsers: coordinatorInfo.allUsers,
        booked: isDenied ? 'N/A' : record.booked,
        vob_likelihood: vob?.likelihood || '—',
        vob_paid_percent: vob?.paidPercent || '—',
      };
    });
  }, [filteredIntakeRecords, getCoordinatorInfo, vobByPrefix]);

  // Export to CSV
  const handleExport = useCallback(() => {
    const headers = [
      'name', 'date', 'checked_in', 'booked', 'reason', 'arriving_date', 'source',
      'date_of_birth', 'insurance', 'prefix', 'policy_id', 'active', 'coordinatorName',
      'phone_number', 'emergency_contact', 'emergency_number', 'facility'
    ];

    const escape = (v: any) => {
      const s = String(v ?? '');
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };

    const formatDate = (val: any) => {
      if (!val) return '';
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return '';
      return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
    };

    const lines = [
      headers.join(','),
      ...(transformedData || []).map((r: any) =>
        headers.map((h) => {
          if (h === 'date' || h === 'date_of_birth' || h === 'arriving_date') {
            return escape(formatDate(r[h]));
          }
          return escape(r[h]);
        }).join(',')
      ),
    ];

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `intake_records_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [transformedData]);

  const showIntakeTable = () => (
    <LayoutComponent
      header={null}
      content={
        <div className="w-full h-full px-2 md:px-4 py-2 md:py-4 relative">
          <ModernTableComponent
            tabs={tabs}
            columns={modernColumns}
            data={transformedData}
            searchPlaceholder="Search clients..."
            onAddNew={handleAddRecord}
            addButtonLabel="Add Record"
            showRowNumbers={true}
            tabFilterFn={tabFilterFn}
            getTabCount={getTabCount}
            onRowClick={handleRowClick}
            userRole={currentProfile?.department || undefined}
            onDeleteRow={handleDeleteRow}
            selectedRow={selectedPatient}
            onExport={['admin', 'owner', 'dev'].includes(currentProfile?.privileges || '') ? handleExport : undefined}
            onCallSheet={currentProfile?.department === 'intake' || ['admin', 'owner', 'dev', 'manager', 'director'].includes(currentProfile?.privileges || '') ? () => setIsCallSheetOpen(true) : undefined}
            isLoading={intakeRecords === null}
            skeletonRows={10}
          />
          {addRecord && (
            <div className='fixed inset-0 z-50 flex items-end md:items-center justify-center'>
              {/* Backdrop */}
              <div
                className='absolute inset-0 bg-black/60 backdrop-blur-sm'
                onClick={handleAddRecord}
              />
              {/* Modal - Full width bottom sheet on mobile, centered modal on desktop */}
              <div className='relative max-h-[90vh] md:max-h-[80vh] w-full md:max-w-md bg-dark-card border-t md:border border-dark-border p-4 md:p-6 rounded-t-2xl md:rounded-2xl overflow-y-auto shadow-2xl animate-fade-in-up'
                   style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
                <div className='w-full flex flex-row justify-between items-center mb-4'>
                  <div className='text-white font-bold text-lg md:text-xl'>New Record</div>
                  <button className='p-2 hover:bg-dark-card-hover active:bg-dark-card-hover rounded-lg transition-colors'>
                    <X onClick={handleAddRecord} height={20} width={20} className='text-gray-400 hover:text-red-400'/>
                  </button>
                </div>
                <IntakeForm onSuccess={handleAddRecord} />
              </div>
            </div>
          )}
        </div>
      }
    />
  );

  const showPopup = () => {
    if (prefixDataDashboard) {
      return (
        <LayoutComponent
          header={
            <div className="h-14 px-4 w-full flex flex-col justify-center bg-dark-card border border-dark-border rounded-xl mb-4">
              <ButtonComponent label="Back" handler={hidePrefixPopup} />
            </div>
          }
          content={
            <div className="h-full w-full overflow-auto">
              {prefixDataDashboard && prefixDataDashboard.length > 0 ? (
                <TableComponent table="HistoricPrefix" columns={historic2Options} records={prefixDataDashboard} users={allUsers} />
              ) : (
                <div className="flex justify-center items-center h-full w-full p-8">
                  <p className="flex text-white text-center">No records available for this prefix!</p>
                </div>
              )}
            </div>
          }
        />
      );
    }
  };

  return (
    <>
      {showPrefix ? showPopup() : showIntakeTable()}
      <PatientDetailPanel
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        patient={selectedPatient}
      />
      <CallSheetModal
        isOpen={isCallSheetOpen}
        onClose={() => setIsCallSheetOpen(false)}
      />
    </>
  );
};

export default HomeScreen;
